import { useState, ReactElement } from "react";

import "./TravelExpenseApplicationDialog.css";
import { Button } from "react-bulma-components";
import styled from "styled-components";

import { PriceLabel } from "components";
import { ConfirmDialog } from "components/ConfirmDialog/ConfirmDialog";
import { APPLICATION_STATUS } from "enums/applicationStatus";
import { showCommentInHTML } from "helpers/comment";
import { MonthSummary, DaySummary } from "models/travelExpense";
import * as Table from "molecules/Table";

const Title = styled.h4`
  margin-top: 1rem;
  margin-bottom: 2rem;
`;

const renderCompletedMessage = () => (
  <div>
    <Title className="title is-4">
      以下の通勤費・交通費の申請が完了しました
    </Title>
  </div>
);

export interface TravelExpenseApplicationDialogProps {
  toggleDialog: () => void;
  isVisible: boolean;
  isUpdateCompleted: boolean;
  isUpdateInProgress: boolean;
  monthSummary: MonthSummary;
  daySummaries: DaySummary[];
  updateApprovalStatus: (
    status: keyof typeof APPLICATION_STATUS,
    comment: string
  ) => void;
  monthString: string;
}

export function TravelExpenseApplicationDialog({
  toggleDialog,
  isVisible,
  isUpdateCompleted,
  isUpdateInProgress,
  monthSummary,
  daySummaries,
  updateApprovalStatus,
  monthString,
}: TravelExpenseApplicationDialogProps): ReactElement | null {
  const [comment, setComment] = useState("");
  const [isOpenConfirmApprovalDialog, setIsOpenConfirmApprovalDialog] =
    useState<boolean>(false);

  const totalCommutingExpenses = daySummaries
    .flatMap((daySummary) => daySummary.commutingExpenses)
    .reduce((acc: number, expense) => acc + expense.totalFare, 0);
  const totalTransportationExpenses = daySummaries
    .flatMap((daySummary) => daySummary.transportationExpenses)
    .reduce((acc: number, expense) => acc + expense.totalFare, 0);

  const initState = () => {
    setComment("");
  };

  const handleClose = () => {
    initState();
    toggleDialog();
  };

  const handleUpdateApprovalStatus = (
    status: keyof typeof APPLICATION_STATUS,
    comment_: string
  ) => {
    updateApprovalStatus(status, comment_);
    initState();
  };

  const existsUnapplied = daySummaries.some(
    (daySummary) =>
      daySummary.applicationStatus.indexOf("未申請") !== -1 ||
      daySummary.applicationStatus.indexOf("申請中") !== -1
  );

  return (
    <div
      className={`modal cw-travel-expense-application-dialog ${
        isVisible ? "is-active" : ""
      }`}
    >
      <div className="modal-background" />
      <div className="modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">{monthString} 通勤費・交通費</p>
          <button
            type="button"
            disabled={isUpdateInProgress}
            className="delete"
            aria-label="close"
            onClick={(_event) => handleClose()}
          />
        </header>
        <section className="modal-card-body">
          {isUpdateCompleted && renderCompletedMessage()}
          <Table.Table>
            <tbody>
              <tr>
                <Table.ThLarge>通勤費 合計金額</Table.ThLarge>
                <Table.Td>
                  <PriceLabel amount={totalCommutingExpenses} />
                </Table.Td>
              </tr>
              <tr>
                <Table.ThLarge>交通費 合計金額</Table.ThLarge>
                <Table.Td>
                  <PriceLabel amount={totalTransportationExpenses} />
                </Table.Td>
              </tr>
              <tr>
                <Table.ThLarge>あなたのコメント</Table.ThLarge>
                <Table.Td>
                  {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    showCommentInHTML(monthSummary.applicantComment!)
                  }
                </Table.Td>
              </tr>
              <tr>
                <Table.ThLarge>承認者のコメント</Table.ThLarge>
                <Table.Td>
                  {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    showCommentInHTML(monthSummary.approverComment!)
                  }
                </Table.Td>
              </tr>
              {!isUpdateCompleted && (
                <tr>
                  <Table.ThLarge>コメント</Table.ThLarge>
                  <Table.Td>
                    <textarea
                      className="textarea"
                      value={comment}
                      onChange={(event) => {
                        setComment(event.target.value);
                      }}
                    />
                  </Table.Td>
                </tr>
              )}
            </tbody>
          </Table.Table>
        </section>
        <footer className="modal-card-foot">
          <Button disabled={isUpdateInProgress} onClick={() => handleClose()}>
            {isUpdateCompleted ? "閉じる" : "キャンセル"}
          </Button>
          {!isUpdateCompleted &&
            monthSummary.applicationStatus === "unapplied" && (
              <>
                <Button
                  color="primary"
                  disabled={isUpdateInProgress || existsUnapplied}
                  onClick={() => setIsOpenConfirmApprovalDialog(true)}
                >
                  申請
                </Button>
                {existsUnapplied && <span>未承認の通勤経路があります</span>}
                {monthSummary.commuterPasses.length > 0 && (
                  <span>通勤定期と重複していないかご確認ください</span>
                )}
              </>
            )}
          {!isUpdateCompleted && monthSummary.applicationStatus === "applying" && (
            <Button
              color="primary"
              disabled={isUpdateInProgress}
              onClick={() => handleUpdateApprovalStatus("unapplied", comment)}
            >
              取下げ
            </Button>
          )}
        </footer>
      </div>
      <ConfirmDialog
        message={`${monthString}の通勤費・交通費の申請をしますがよろしいですか？`}
        isOpen={isOpenConfirmApprovalDialog}
        closeDialog={() => setIsOpenConfirmApprovalDialog(false)}
        confirmedAction={() => {
          handleUpdateApprovalStatus("applying", comment);
        }}
      />
    </div>
  );
}
